/**
 * Via Logos - Achievements Context
 *
 * Tracks user progress through Bible verse exploration
 * with spiritually meaningful achievements
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Achievement, AchievementId, GameplayStats } from '../constants/types';

// Prerequisite: achievement IDs that must be unlocked before this one
interface AchievementWithPrereqs extends Omit<Achievement, 'unlocked' | 'unlockedAt'> {
  prerequisites?: AchievementId[];
}

// Achievement definitions with prerequisites (Bible-themed)
export const ACHIEVEMENT_DEFINITIONS: AchievementWithPrereqs[] = [
  {
    id: 'first_step',
    name: 'First Step',
    description: 'Complete 20 turns in a verse walkthrough',
    icon: '',
    requirement: { type: 'turns', value: 20 },
  },
  {
    id: 'seeker',
    name: 'Seeker',
    description: 'Explore Scripture for 30 minutes total',
    icon: '',
    requirement: { type: 'playtime', value: 30 * 60 * 1000 }, // 30 minutes in ms
  },
  {
    id: 'disciple',
    name: 'Disciple',
    description: 'Experience 5 different Bible verses',
    icon: '',
    requirement: { type: 'verses', value: 5 },
    prerequisites: ['first_step'], // Must complete First Step first
  },
  {
    id: 'pilgrim',
    name: 'Pilgrim',
    description: 'Complete 100 turns across all walkthroughs',
    icon: '',
    requirement: { type: 'turns', value: 100 },
    prerequisites: ['first_step'],
  },
  {
    id: 'scripture_explorer',
    name: 'Scripture Explorer',
    description: 'Explore for 2 hours total',
    icon: '',
    requirement: { type: 'playtime', value: 2 * 60 * 60 * 1000 }, // 2 hours in ms
    prerequisites: ['seeker'], // Must earn Seeker first
  },
  {
    id: 'faithful_journeyer',
    name: 'Faithful Journeyer',
    description: 'Complete 500 turns across all walkthroughs',
    icon: '',
    requirement: { type: 'turns', value: 500 },
    prerequisites: ['pilgrim'], // Must earn Pilgrim first
  },
  {
    id: 'devoted_student',
    name: 'Devoted Student',
    description: 'Spend 1 hour in a single session',
    icon: '',
    requirement: { type: 'single_session', value: 60 * 60 * 1000 }, // 1 hour in ms
    prerequisites: ['seeker'],
  },
  {
    id: 'wise_sage',
    name: 'Wise Sage',
    description: 'Experience 20 different Bible verses',
    icon: '',
    requirement: { type: 'verses', value: 20 },
    prerequisites: ['disciple'],
  },
  {
    id: 'light_bearer',
    name: 'Light Bearer',
    description: 'Read 5,000 words in walkthroughs',
    icon: '',
    requirement: { type: 'words', value: 5000 },
    prerequisites: ['pilgrim'],
  },
  {
    id: 'truth_seeker',
    name: 'Truth Seeker',
    description: 'Read 20,000 words across all walkthroughs',
    icon: '',
    requirement: { type: 'total_words', value: 20000 },
    prerequisites: ['light_bearer'],
  },
  {
    id: 'eternal_pilgrim',
    name: 'Eternal Pilgrim',
    description: 'Journey through Scripture for 10 hours total',
    icon: '',
    requirement: { type: 'playtime', value: 10 * 60 * 60 * 1000 }, // 10 hours
    prerequisites: ['scripture_explorer'],
  },
];

interface AchievementsContextType {
  achievements: Achievement[];
  stats: GameplayStats;
  unlockedCount: number;
  totalCount: number;
  checkAchievements: () => void;
  resetAchievements: () => void;
  resetProgress: () => void;
  checkDependencies: () => boolean;
  recordTurn: () => void;
  recordVerseExplored: (verseReference: string) => void;
  recordWordsWritten: (wordCount: number) => void;
  setGameActive: (active: boolean) => void;
  isGameActive: boolean;
}

const ACHIEVEMENTS_STORAGE_KEY = 'via_logos_achievements';
const STATS_STORAGE_KEY = 'via_logos_gameplay_stats';

const AchievementsContext = createContext<AchievementsContextType | undefined>(undefined);

const defaultStats: GameplayStats = {
  totalTurns: 0,
  totalPlaytimeMs: 0,
  totalVersesExplored: 0,
  longestSessionMs: 0,
  currentSessionMs: 0,
  totalWords: 0,
  versesExplored: [],
  uniqueVerses: 0,
};

export const AchievementsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [achievements, setAchievements] = useState<Achievement[]>(() =>
    ACHIEVEMENT_DEFINITIONS.map(def => ({ ...def, unlocked: false }))
  );
  const [stats, setStats] = useState<GameplayStats>(defaultStats);
  const [isGameActive, setIsGameActive] = useState(false);

  // Ref to track the current session start time
  const sessionStartTimeRef = useRef(Date.now());

  // Use ref to avoid stale closures in the timer
  const statsRef = useRef(stats);
  statsRef.current = stats;

  // Load saved achievements and stats
  useEffect(() => {
    const loadData = async () => {
      try {
        let savedAchievements: string | null = null;
        let savedStats: string | null = null;

        savedAchievements = localStorage.getItem(ACHIEVEMENTS_STORAGE_KEY);
        savedStats = localStorage.getItem(STATS_STORAGE_KEY);

        if (savedAchievements) {
          const parsedAchievements = JSON.parse(savedAchievements);
          setAchievements(parsedAchievements);
        }

        if (savedStats) {
          // Merge saved stats with default to handle migrations
          const parsedStats = JSON.parse(savedStats);
          setStats(prevStats => ({
            ...defaultStats,
            ...parsedStats,
            versesExplored: parsedStats.versesExplored || [],
          }));
        }
      } catch (error) {
        console.error('Failed to load achievements from storage', error);
      }
    };
    loadData();
  }, []);

  // Save achievements to storage
  const saveAchievements = useCallback((newAchievements: Achievement[]) => {
    try {
      const jsonValue = JSON.stringify(newAchievements);
      localStorage.setItem(ACHIEVEMENTS_STORAGE_KEY, jsonValue);
    } catch (error) {
      console.error('Failed to save achievements to storage', error);
    }
  }, []);

  // Save stats to storage
  const saveStats = useCallback((newStats: GameplayStats) => {
    try {
      const jsonValue = JSON.stringify(newStats);
      localStorage.setItem(STATS_STORAGE_KEY, jsonValue);
    } catch (error) {
      console.error('Failed to save stats to storage', error);
    }
  }, []);

  // Check if prerequisites are met for an achievement
  const arePrerequisitesMet = useCallback(
    (achievement: AchievementWithPrereqs, currentAchievements: Achievement[]): boolean => {
      if (!achievement.prerequisites || achievement.prerequisites.length === 0) return true;

      return achievement.prerequisites.every(prereqId =>
        currentAchievements.some(a => a.id === prereqId && a.unlocked)
      );
    },
    []
  );

  // Check and unlock achievements based on current stats
  const checkAchievements = useCallback(() => {
    setAchievements(prevAchievements => {
      const updated = prevAchievements.map((achievement, index) => {
        // Skip if already unlocked
        if (achievement.unlocked) return achievement;

        // Get the full definition with prerequisites
        const def = ACHIEVEMENT_DEFINITIONS[index];
        if (!def) return achievement;

        // Check prerequisites first
        if (!arePrerequisitesMet(def, prevAchievements)) {
          return { ...achievement, lockedReason: 'Complete previous achievements first' };
        }

        let shouldUnlock = false;
        const { type, value } = achievement.requirement;

        switch (type) {
          case 'turns':
            shouldUnlock = statsRef.current.totalTurns >= value;
            break;
          case 'playtime':
            shouldUnlock = statsRef.current.totalPlaytimeMs >= value;
            break;
          case 'verses':
            shouldUnlock = statsRef.current.totalVersesExplored >= value;
            break;
          case 'single_session':
            shouldUnlock = statsRef.current.currentSessionMs >= value;
            break;
          case 'words':
            // Track words per walkthrough (simplified - uses total for now)
            shouldUnlock = statsRef.current.totalWords >= value;
            break;
          case 'total_words':
            shouldUnlock = statsRef.current.totalWords >= value;
            break;
        }

        if (shouldUnlock) {
          return { ...achievement, unlocked: true, unlockedAt: Date.now() };
        }
        return { ...achievement, lockedReason: undefined };
      });

      // Only save if something changed
      const hasChanges = updated.some((a, i) => a.unlocked !== prevAchievements[i].unlocked);
      if (hasChanges) {
        saveAchievements(updated);
      }

      return updated;
    });
  }, [arePrerequisitesMet, saveAchievements]);

  // Timer-based playtime tracking - works on both web and native
  useEffect(() => {
    const timer = setInterval(() => {
      if (isGameActive) {
        const now = Date.now();
        const sessionDuration = now - sessionStartTimeRef.current;

        setStats(prevStats => {
          const newStats = {
            ...prevStats,
            totalPlaytimeMs: prevStats.totalPlaytimeMs + 1000, // Add 1 second
            currentSessionMs: prevStats.currentSessionMs + 1000,
            longestSessionMs: Math.max(
              prevStats.longestSessionMs,
              prevStats.currentSessionMs + 1000
            ),
          };

          // Save periodically (every 30 seconds) to avoid excessive writes
          if (newStats.totalPlaytimeMs % 30000 < 1000) {
            saveStats(newStats);
          }

          return newStats;
        });
      }
    }, 1000); // Update every second

    return () => clearInterval(timer);
  }, [isGameActive, saveStats]);

  // Record a turn taken
  const recordTurn = useCallback(() => {
    setStats(prevStats => {
      const newStats = { ...prevStats, totalTurns: prevStats.totalTurns + 1 };
      saveStats(newStats);
      return newStats;
    });
  }, [saveStats]);

  // Record words written
  const recordWordsWritten = useCallback(
    (wordCount: number) => {
      setStats(prevStats => {
        const newStats = { ...prevStats, totalWords: prevStats.totalWords + wordCount };
        saveStats(newStats);
        return newStats;
      });
    },
    [saveStats]
  );

  // Record a verse explored
  const recordVerseExplored = useCallback(
    (verseReference: string) => {
      setStats(prevStats => {
        const versesExplored = prevStats.versesExplored.includes(verseReference)
          ? prevStats.versesExplored
          : [...prevStats.versesExplored, verseReference];

        const newStats = {
          ...prevStats,
          totalVersesExplored: versesExplored.length,
          versesExplored,
        };
        saveStats(newStats);
        return newStats;
      });
    },
    [saveStats]
  );

  // Set game active state (called by App when walkthrough starts/stops)
  const setGameActive = useCallback(
    (active: boolean) => {
      setIsGameActive(active);
      if (active) {
        // Starting a new session
        sessionStartTimeRef.current = Date.now();
        setStats(prevStats => ({ ...prevStats, currentSessionMs: 0 }));
      } else {
        // Ending session - save final stats
        setStats(prevStats => {
          saveStats(prevStats);
          return { ...prevStats, currentSessionMs: 0 };
        });
      }
    },
    [saveStats]
  );

  // Reset all achievements (for testing)
  const resetAchievements = useCallback(() => {
    const resetAchievementsList = ACHIEVEMENT_DEFINITIONS.map(def => ({
      ...def,
      unlocked: false,
    }));
    setAchievements(resetAchievementsList);
    setStats(defaultStats);
    saveAchievements(resetAchievementsList);
    saveStats(defaultStats);
  }, [saveAchievements, saveStats]);

  // Alias for resetAchievements (used in UI)
  const resetProgress = useCallback(() => {
    resetAchievements();
  }, [resetAchievements]);

  // Check if all achievement dependencies are met
  const checkDependencies = useCallback((): boolean => {
    // Check if all achievements with prerequisites have their prerequisites unlocked
    for (const def of ACHIEVEMENT_DEFINITIONS) {
      if (def.prerequisites && def.prerequisites.length > 0) {
        const achievement = achievements.find(a => a.id === def.id);
        if (achievement && achievement.unlocked) {
          // If achievement is unlocked, verify prerequisites are also unlocked
          const prereqsUnlocked = def.prerequisites.every(prereqId =>
            achievements.some(a => a.id === prereqId && a.unlocked)
          );
          if (!prereqsUnlocked) {
            return false;
          }
        }
      }
    }
    return true;
  }, [achievements]);

  // Check achievements whenever stats change
  useEffect(() => {
    checkAchievements();
  }, [stats, checkAchievements]);

  const unlockedCount = useMemo(() => achievements.filter(a => a.unlocked).length, [achievements]);
  const totalCount = achievements.length;

  return (
    <AchievementsContext.Provider
      value={{
        achievements,
        stats,
        unlockedCount,
        totalCount,
        checkAchievements,
        resetAchievements,
        resetProgress,
        checkDependencies,
        recordTurn,
        recordVerseExplored,
        recordWordsWritten,
        setGameActive,
        isGameActive,
      }}
    >
      {children}
    </AchievementsContext.Provider>
  );
};

export const useAchievements = () => {
  const context = useContext(AchievementsContext);
  if (!context) throw new Error('useAchievements must be used within an AchievementsProvider');
  return context;
};
